import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Heart,
  Gem,
} from "lucide-react";
import confetti from "canvas-confetti";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playPop, playSuccess, playError } from "@/lib/studySfx";
import { useVictoryMusic } from "@/hooks/useVictoryMusic";
import { GameResultsScreen } from "@/components/study/GameResultsScreen";

const CRYSTALS_BASE = 5;
const MAX_LIVES = 3;
const MAX_ROUNDS = 12;
const SUCCESS_DELAY_MS = 1500;

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** One option in the 2x2 grid: either the 1 correct card (term + correct translation) or 3 incorrect (term + wrong translation). */
interface GridOption {
  card: Card;
  /** Shown translation: for incorrect cards this is from another card. */
  displayBack: string;
  /** True for the single card with the correct term–translation pair. */
  isCorrect: boolean;
  index: number;
}

interface IqModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

/** Floating +N crystals animation. */
function FloatingCrystals({
  amount,
  onComplete,
}: {
  amount: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-3xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-8 h-8" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

const cardContainerVariants = {
  hidden: { opacity: 0 },
  visible: (i: number) => ({
    opacity: 1,
    transition: { delay: i * 0.08 },
  }),
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.1, type: "spring", stiffness: 300, damping: 24 },
  }),
  exitNormal: { opacity: 0, scale: 0, transition: { duration: 0.35 } },
};

export default function IqMode({
  cards,
  lang: deckLang,
  onBack,
  onProgressComplete,
}: IqModeProps) {
  const { t } = useTranslation();
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const [round, setRound] = useState(0);
  const [lives, setLives] = useState(MAX_LIVES);
  const [roundState, setRoundState] = useState<{
    options: GridOption[];
    correctIndex: number;
  } | null>(null);
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  const [wrongShakeIndex, setWrongShakeIndex] = useState<number | null>(null);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [sessionCrystals, setSessionCrystals] = useState(0);

  const needCards = filteredCards.length < 5;

  useVictoryMusic({
    isGameFinished,
    isSoundEnabled,
    gameVolume,
  });


  /** Pick a wrong translation for a card (from deck, must differ from the card's real back). */
  const getWrongBackFor = useCallback((card: Card, pool: Card[]): string => {
    const others = pool.filter((c) => (c.back || "").trim() !== (card.back || "").trim());
    return others.length > 0
      ? (others[Math.floor(Math.random() * others.length)]?.back ?? card.back)
      : card.back;
  }, []);

  const buildRound = useCallback(() => {
    if (filteredCards.length < 5) return;
    const shuffled = shuffleArray(filteredCards);
    const four = shuffled.slice(0, 4);
    const correctIndex = Math.floor(Math.random() * 4);
    const options: GridOption[] = four.map((card, i) => ({
      card,
      displayBack: i === correctIndex ? card.back : getWrongBackFor(card, shuffled),
      isCorrect: i === correctIndex,
      index: i,
    }));
    setRoundState({ options, correctIndex });
    setPickedIndex(null);
    setWrongShakeIndex(null);
  }, [filteredCards, getWrongBackFor]);

  useEffect(() => {
    if (needCards || gameOver || isGameFinished) return;
    buildRound();
  }, [round, needCards, gameOver, isGameFinished, buildRound]);

  const handlePick = useCallback(
    (optionIndex: number) => {
      if (!roundState || pickedIndex !== null) return;
      const option = roundState.options[optionIndex];
      if (!option) return;

      if (useSoundStore.getState().isVoiceEnabled && (option.card?.front || "").trim()) {
        void playAudio({
          card: option.card,
          side: "front",
          deckLang: deckLang,
          volume: voiceVolume,
        });
      }
      if (isSoundEnabled) playPop(gameVolume * 0.6);

      if (option.isCorrect) {
        setPickedIndex(optionIndex);
        const crystalAmount = CRYSTALS_BASE + Math.min(round, 10);
        const result = addCrystals(
          crystalAmount,
          t("iqMode.rewardReason"),
          { isLearningAction: true, silent: true }
        );
        const totalAdd =
          typeof result === "object" ? result.totalAdd : crystalAmount;
        setFloatingCrystals(totalAdd);
        setSessionCrystals((prev) => prev + totalAdd);
        if (isSoundEnabled) playSuccess(gameVolume * 0.7);
        confetti({
          colors: ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b"],
          origin: { y: 0.7 },
          particleCount: 100,
          spread: 55,
        });
        const nextRound = round + 1;
        if (onProgressComplete) {
          const pct = Math.min(100, Math.round((nextRound / MAX_ROUNDS) * 100));
          onProgressComplete(pct);
        }
        setTimeout(() => {
          setFloatingCrystals(null);
          setRound(nextRound);
          finishStudySession(1, false);
          if (nextRound >= MAX_ROUNDS) setIsGameFinished(true);
        }, SUCCESS_DELAY_MS);
      } else {
        setWrongShakeIndex(optionIndex);
        if (isSoundEnabled) playError(gameVolume * 0.8);
        const newLives = lives - 1;
        setLives(newLives);
        setTimeout(() => setWrongShakeIndex(null), 450);
        if (newLives <= 0) {
          if (onProgressComplete) {
            const pct = Math.min(100, Math.round((round / MAX_ROUNDS) * 100));
            onProgressComplete(pct);
          }
          setGameOver(true);
        }
      }
    },
    [
      roundState,
      pickedIndex,
      lives,
      isSoundEnabled,
      gameVolume,
      isVoiceEnabled,
      voiceVolume,
      deckLang,
      addCrystals,
      t,
      finishStudySession,
      onProgressComplete,
      round,
    ]
  );

  const handleRestart = useCallback(() => {
    setGameOver(false);
    setLives(MAX_LIVES);
    setRound(0);
    setRoundState(null);
    setPickedIndex(null);
    setWrongShakeIndex(null);
  }, []);

  const handlePlayAgain = useCallback(() => {
    setIsGameFinished(false);
    setSessionCrystals(0);
    setGameOver(false);
    setLives(MAX_LIVES);
    setRound(0);
    setRoundState(null);
    setPickedIndex(null);
    setWrongShakeIndex(null);
  }, []);

  const handleBackToDeck = useCallback(() => {
    onBack?.();
  }, [onBack]);

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1 flex items-center gap-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : null}
        <div
          className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]"
          aria-label={t("iqMode.lives")}
        >
          {Array.from({ length: MAX_LIVES }).map((_, i) => (
            <Heart
              key={i}
              className={`w-5 h-5 shrink-0 ${
                i < lives
                  ? "text-rose-500 fill-rose-500 dark:text-rose-400 dark:fill-rose-400"
                  : "text-[hsl(var(--muted))] opacity-40"
              }`}
            />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isSoundEnabled ? t("common.muteMusic") : t("common.unmuteMusic")
          }
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={
            isVoiceEnabled
              ? t("common.voiceModeOn")
              : t("common.voiceModeOff")
          }
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (needCards) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("iqMode.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("iqMode.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4 pb-10">
        {headerRow}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center"
        >
          <p className="text-xl font-bold text-[hsl(var(--foreground))] mb-2">
            {t("iqMode.gameOver")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
            {t("iqMode.gameOverHint")}
          </p>
          <button
            type="button"
            onClick={handleRestart}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity"
          >
            {t("iqMode.restart")}
          </button>
        </motion.div>
      </div>
    );
  }

  if (isGameFinished) {
    return (
      <GameResultsScreen
        sessionCrystals={sessionCrystals}
        onPlayAgain={handlePlayAgain}
        onBackToDeck={handleBackToDeck}
        headerRow={headerRow}
      />
    );
  }

  const options = roundState?.options ?? [];

  return (
    <div className="flex flex-col w-full max-w-2xl mx-auto px-4 pb-10 relative">
      <AnimatePresence>
        {floatingCrystals != null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}
      </AnimatePresence>

      {headerRow}

      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">
          {t("iqMode.round", { current: round + 1, defaultValue: "Round {{current}}" })}
        </span>
      </div>

      <p className="text-center text-sm font-medium text-[hsl(var(--muted-foreground))] mb-6">
        {t("iqMode.findCorrect", { defaultValue: "Find the card with the correct translation." })}
      </p>

      <motion.div
        className="grid grid-cols-2 gap-4 sm:gap-6"
        variants={cardContainerVariants}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence mode="popLayout">
          {options.map((opt, i) => {
            const isRevealed =
              pickedIndex !== null &&
              (opt.isCorrect || pickedIndex === i);
            const shouldExit =
              pickedIndex !== null && !opt.isCorrect;
            const isWrongShake = wrongShakeIndex === i;

            return (
              <motion.div
                key={`${opt.card.id ?? i}-${opt.index}`}
                layout
                custom={i}
                variants={cardItemVariants}
                initial="hidden"
                animate="visible"
                exit={shouldExit ? "exitNormal" : undefined}
                className="flex justify-center items-stretch"
              >
                <motion.button
                  type="button"
                  layout
                  disabled={pickedIndex !== null}
                  onClick={() => handlePick(i)}
                  className={`
                    group relative w-full min-h-[140px] rounded-2xl overflow-hidden
                    bg-white/70 dark:bg-slate-900/70 backdrop-blur-md shadow-xl
                    border-2 text-left p-4 sm:p-5
                    transition-colors duration-200
                    disabled:pointer-events-none
                    ${isWrongShake ? "animate-shake border-red-500 bg-red-500/20 dark:bg-red-900/30" : ""}
                    ${isRevealed && opt.isCorrect ? "border-emerald-500 bg-emerald-500/30 dark:bg-emerald-500/20" : ""}
                    ${!isRevealed && !isWrongShake ? "border-[hsl(var(--border))]" : ""}
                  `}
                  whileHover={
                    pickedIndex === null && !isWrongShake
                      ? { scale: 1.02 }
                      : undefined
                  }
                  whileTap={
                    pickedIndex === null && !isWrongShake
                      ? { scale: 0.98 }
                      : undefined
                  }
                  style={{ transformStyle: "preserve-3d", perspective: 1000 }}
                >
                  {/* Holographic gradient border on hover (outer glow) */}
                  {!isRevealed && !isWrongShake && (
                    <span
                      className="absolute inset-[-2px] rounded-[18px] -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #f97316 100%)",
                        filter: "blur(8px)",
                      }}
                    />
                  )}
                  <span
                    className="absolute inset-0 rounded-2xl -z-10 opacity-0 group-hover:opacity-70 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(236,72,153,0.15) 50%, rgba(249,115,22,0.15) 100%)",
                    }}
                  />

                  {/* 3D flip container: rotateY 180 when correct card revealed */}
                  <motion.div
                    className="relative z-0 w-full h-full"
                    style={{
                      transformStyle: "preserve-3d",
                      perspective: 1000,
                    }}
                    initial={false}
                    animate={{
                      rotateY: isRevealed && opt.isCorrect ? 180 : 0,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 180,
                      damping: 22,
                    }}
                  >
                    {/* Front face: term + displayed translation (wrong for incorrect cards) */}
                    <div
                      className="absolute inset-0 flex flex-col"
                      style={{
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                      }}
                    >
                      <span className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
                        {t("iqMode.termLabel", { defaultValue: "Term" })}
                      </span>
                      <span className="text-lg font-bold text-[hsl(var(--foreground))] mt-0.5 break-words">
                        {opt.card.front}
                      </span>
                      <span className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mt-3">
                        {t("iqMode.translationLabel", { defaultValue: "Translation" })}
                      </span>
                      <span className="text-base text-[hsl(var(--foreground))] mt-0.5 break-words">
                        {opt.displayBack}
                      </span>
                    </div>
                    {/* Back face: same term + correct translation (visible after flip for correct card) */}
                    <div
                      className="absolute inset-0 flex flex-col"
                      style={{
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      <span className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
                        {t("iqMode.termLabel", { defaultValue: "Term" })}
                      </span>
                      <span className="text-lg font-bold text-[hsl(var(--foreground))] mt-0.5 break-words">
                        {opt.card.front}
                      </span>
                      <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mt-3">
                        {t("iqMode.actualTranslationLabel", { defaultValue: "Actual translation" })}
                      </span>
                      <span className="text-base text-emerald-700 dark:text-emerald-300 font-medium mt-0.5 break-words">
                        {opt.card.back}
                      </span>
                    </div>
                  </motion.div>
                </motion.button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
